/**
 * AuthStore - Complex async actions extracted for readability
 *
 * setFromSSO, getFreshTokenForSSO, and checkAuth are async actions that
 * interact with the backend and require multiple steps with branching logic.
 */

import type { AuthState, User } from '../types'
import {
  setAuthCookie,
  setRefreshCookie,
  setOrgCookie,
  getAuthCookie,
  getRefreshCookie,
  getOrgCookie,
} from '../cookieAuth'
import { migrateLegacyAuth } from '../legacyBridge'
import {
  isTokenExpired,
  isTokenExpiringSoon,
  fetchUserProfile,
  fetchUserOrganisations,
  refreshAccessToken,
  resolveActiveOrg,
} from './apiHelpers'
import { broadcastIdentityChange } from '../session-manager/tokenSync'
import { reconcileIdentity } from '../identityReconcile'
import { needsAuthorityRefetch } from '../authorityOrgStamp'
import { runOnIdentitySwitch } from '../identityHygiene'

/**
 * Resolve the best available refresh token from Zustand store or cookie.
 * SSO flows only pass the access token — the refresh token remains in the
 * shared cookie but is NOT synced to the Zustand store.  Falling back to
 * the cookie ensures hubs can still refresh after SSO navigation.
 */
function resolveRefreshToken(storeRefreshToken: string | null): string | null {
  return storeRefreshToken || getRefreshCookie()
}

type GetState = () => AuthState
type SetState = (partial: Partial<AuthState> | ((state: AuthState) => Partial<AuthState>)) => void

/**
 * Authenticate from an SSO token: validate, fetch profile, set store + cookies
 */
export async function performSetFromSSO(
  token: string,
  orgId: string | null | undefined,
  orgName: string | null | undefined,
  get: GetState,
  set: SetState,
): Promise<boolean> {
  console.log('[auth] setFromSSO: starting')
  set({ isLoading: true, error: null })

  try {
    if (isTokenExpired(token)) {
      console.log('[auth] setFromSSO: token expired')
      set({ isLoading: false, error: 'Token expired' })
      return false
    }

    const user = await fetchUserProfile(token, orgId)
    if (!user) {
      console.log('[auth] setFromSSO: fetchUserProfile returned null')
      set({ isLoading: false, error: 'Failed to fetch user profile' })
      return false
    }

    if (orgId) user.organisation_id = orgId
    if (orgName) user.organisation_name = orgName

    const previousUserId = get().user?.id ?? get().user?.email ?? null
    const newUserId = user.id ?? user.email ?? null
    const identitySwitched = previousUserId !== newUserId

    // SAME-TAB session hygiene on a store-driven identity switch
    // (MR-2, BUG-2026-06-02 follow-up). The tenant auth gate already
    // wipes the previous user's per-user/tenant localStorage keys
    // (profile pill, last kanban board, persisted tab strip, tenant
    // pin) inside createApiAuthGate.applySession; the MASTER SPA's SSO
    // path (UnifiedSSOPage → setFromSSO) never did, so a same-tab
    // account switch could leave the previous user's cached UI bits
    // behind. We run hygiene BEFORE writing the new cookie/store state
    // — same ordering the tenant gate uses — because the wipe list
    // includes token-shaped keys and we don't want to clobber the
    // session we're about to set. enforceSessionHygiene lives in
    // @quotech/ui (which depends on @quotech/auth), so it's injected at
    // app boot via setOnIdentitySwitch rather than imported here.
    if (identitySwitched) {
      runOnIdentitySwitch(newUserId, 'authStore.performSetFromSSO.identity-switch')
    }

    setAuthCookie(token)
    if (user.organisation_id) setOrgCookie(user.organisation_id, user.organisation_name || undefined)

    const existingRefresh = getRefreshCookie()
    set({
      user, accessToken: token, refreshToken: existingRefresh,
      organisationId: user.organisation_id || null,
      organisationName: user.organisation_name || null,
      isAuthenticated: true, isLoading: false, error: null,
    })

    // Cross-tab identity broadcast (BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT).
    // SSO is the most likely entry point for "log into another client in
    // a new tab" because hub deep links always SSO-exchange before render,
    // so EVERY sibling tab on .quotech.ai needs to know the cookie jar's
    // user just changed.
    if (identitySwitched) {
      broadcastIdentityChange({
        userId: newUserId,
        orgId: user.organisation_id ?? null,
        reason: 'authStore.performSetFromSSO',
      })
    }
    return true
  } catch (err) {
    set({ isLoading: false, error: err instanceof Error ? err.message : 'SSO failed' })
    return false
  }
}

/**
 * Get a fresh token for SSO navigation, refreshing if needed
 */
export async function performGetFreshTokenForSSO(
  get: GetState,
  set: SetState,
): Promise<string | null> {
  const { accessToken, refreshToken: storeRefreshToken } = get()
  if (!accessToken) return null

  const tokenFullyExpired = isTokenExpired(accessToken, 0)
  if (!isTokenExpiringSoon(accessToken) && !tokenFullyExpired) return accessToken

  console.log('[auth] getFreshTokenForSSO: token expiring soon or expired, refreshing')
  const effectiveRefresh = resolveRefreshToken(storeRefreshToken)
  if (!effectiveRefresh) {
    if (tokenFullyExpired) return null
    return accessToken
  }

  try {
    const newTokens = await refreshAccessToken(effectiveRefresh)
    if (newTokens) {
      setAuthCookie(newTokens.access)
      if (newTokens.refresh) setRefreshCookie(newTokens.refresh)
      set({ accessToken: newTokens.access, refreshToken: newTokens.refresh || effectiveRefresh })
      return newTokens.access
    }
  } catch (err) {
    console.warn('[auth] getFreshTokenForSSO: refresh failed', err)
  }

  return tokenFullyExpired ? null : accessToken
}

/**
 * Full auth check: cookie > store > legacy migration > token refresh > org resolution
 */
export async function performCheckAuth(get: GetState, set: SetState): Promise<boolean> {
  const cookieToken = getAuthCookie()
  const cookieRefresh = getRefreshCookie()
  const cookieOrg = getOrgCookie()
  const { accessToken: storedToken, refreshToken: storedRefresh } = get()

  let accessToken = cookieToken || storedToken
  let refreshToken = cookieRefresh || storedRefresh

  // Legacy migration fallback
  if (!accessToken) {
    const legacy = migrateLegacyAuth()
    if (legacy?.tokens?.access) {
      console.log('[@quotech/auth] checkAuth: found legacy auth, migrating')
      accessToken = legacy.tokens.access
      refreshToken = legacy.tokens.refresh || refreshToken
      setAuthCookie(accessToken)
      if (refreshToken) setRefreshCookie(refreshToken)
      if (legacy.orgId) setOrgCookie(legacy.orgId, legacy.orgName || undefined)
      set({
        accessToken, refreshToken, user: legacy.user,
        organisationId: legacy.orgId, organisationName: legacy.orgName,
        isAuthenticated: true, isLoading: false,
      })
      return true
    }
  }

  if (!accessToken) { set({ isAuthenticated: false, isLoading: false }); return false }

  // Sync cookie token to store
  if (cookieToken && !storedToken) {
    set({
      accessToken: cookieToken,
      refreshToken: cookieRefresh || storedRefresh,
      organisationId: cookieOrg?.id || get().organisationId,
      organisationName: cookieOrg?.name || get().organisationName,
    })
  }

  // Token valid — resolve org context
  if (!isTokenExpired(accessToken)) {
    return resolveOrgContext(accessToken, get, set, cookieOrg)
  }

  // Token expired — attempt refresh
  if (refreshToken) {
    const newTokens = await refreshAccessToken(refreshToken)
    if (newTokens) {
      setAuthCookie(newTokens.access)
      setRefreshCookie(newTokens.refresh)
      set({ accessToken: newTokens.access, refreshToken: newTokens.refresh, isAuthenticated: true, isLoading: false })
      return true
    }
  }

  get().clearAuth()
  return false
}

/** Resolve organisation context (id + name) from cookies, store, or backend API */
async function resolveOrgContext(
  accessToken: string,
  get: GetState,
  set: SetState,
  cookieOrg: { id: string; name: string } | null,
): Promise<boolean> {
  let resolvedOrgId = (get().organisationId || cookieOrg?.id || null) as string | null
  let resolvedOrgName = (() => {
    const raw = (get().organisationName || cookieOrg?.name || null) as string | null
    return typeof raw === 'string' && raw.trim() ? raw.trim() : null
  })()

  // Validate the cached org id against the user's REAL memberships
  // before trusting it. The shared `.quotech.ai` `quotech_org` cookie
  // can carry an org id from a prior session/tenant; blindly trusting
  // it mis-scopes the RBAC permissions call and locks the user out of
  // hubs (even an owner) via a "no modules" -> HubGuard Access Denied.
  // Observed: cfo.quotech.ai sent org_id=1 ("Quotech Demo") for the
  // owner of org 4 ("Bravada Group"). See resolveActiveOrg +
  // BUG-2026-06-05-STALE-ORG-COOKIE (current-tenant-drift family,
  // BUG-0020/0021/0028).
  const orgs = await fetchUserOrganisations(accessToken)
  const picked = resolveActiveOrg(resolvedOrgId, resolvedOrgName, orgs)
  if (!picked.id) {
    set({ isAuthenticated: false, isLoading: false, error: 'No organisation found. Please contact support or re-login.' })
    return false
  }
  if (picked.id !== resolvedOrgId) {
    console.warn(
      `[@quotech/auth] resolveOrgContext: cached org ${resolvedOrgId ?? '(none)'} not in ` +
      `memberships [${(orgs || []).map((o) => o.id).join(', ')}] — using ${picked.id}`,
    )
  }
  resolvedOrgId = picked.id
  resolvedOrgName = picked.name || resolvedOrgName

  setOrgCookie(resolvedOrgId, resolvedOrgName || undefined)
  set({ organisationId: resolvedOrgId, organisationName: resolvedOrgName, isAuthenticated: true, isLoading: false })

  // Hydrate user if missing OR if the persisted user does not belong to
  // this access token. The latter is the cold-load identity-adoption gap
  // (BUG-2026-06-02): a stale persisted `user` (e.g. dev@quotech.ai) used
  // to be trusted whenever it was present, so a tab that adopted a cookie
  // token for a DIFFERENT account would render the stale identity. We now
  // reconcile the token's `sub` against the persisted user (pure, reuses
  // decodeJwtSub) and re-fetch the real profile on a confirmed mismatch.
  // `indeterminate` (undecodable token) keeps the prior trusting merge —
  // a decode failure is NOT a confirmed identity change (2026-05-19
  // false-positive lesson).
  const existingUser = get().user
  const { verdict: identityVerdict, tokenSub } = reconcileIdentity({
    token: accessToken,
    user: existingUser,
  })
  const identityMismatch = identityVerdict === 'mismatch'
  // Right account, wrong ORG. reconcileIdentity only compares the JWT subject,
  // so a persisted user who switched org passes it while still carrying the
  // previous org's membership-scoped display address. Nothing else on this
  // path would notice: both refresh endpoints return tokens only.
  const authorityStale =
    !identityMismatch && needsAuthorityRefetch(existingUser, resolvedOrgId)
  if (!existingUser || identityMismatch || authorityStale) {
    if (authorityStale) {
      console.log(
        `[@quotech/auth] checkAuth: cached identity was resolved for org ` +
        `${existingUser?.authority_org_id || '(unstamped)'} but the active org ` +
        `is ${resolvedOrgId} — re-fetching profile for the active org.`,
      )
    }
    if (identityMismatch) {
      console.warn(
        '[@quotech/auth] checkAuth: persisted user does not match the access ' +
        'token — dropping stale identity and re-fetching profile from token.',
      )
      // SAME-TAB session hygiene (MR-2): the persisted user is stale and
      // about to be replaced by the token's real owner. Wipe the previous
      // user's per-user/tenant localStorage keys BEFORE the refetch so the
      // brief window before set({ user }) lands can't surface a mix of the
      // old user's cached UI bits and the new identity. Mirrors the tenant
      // gate ordering (wipe-before-write); injected at app boot.
      runOnIdentitySwitch(tokenSub, 'authStore.checkAuth.identity-mismatch')
      // Tell sibling tabs the cookie jar's user changed so their caches
      // are invalidated too (same broadcast every other identity-mutation
      // site uses). Fired before the refetch so the signal isn't lost if
      // the network call hangs.
      try {
        broadcastIdentityChange({
          userId: tokenSub,
          orgId: resolvedOrgId,
          reason: 'authStore.checkAuth.identity-mismatch',
        })
      } catch { /* broadcast must never break auth */ }
    }
    try {
      const user = await fetchUserProfile(accessToken, resolvedOrgId)
      if (user) {
        user.organisation_id = resolvedOrgId
        user.organisation_name = resolvedOrgName || ''
        set({ user })
      } else if (identityMismatch) {
        // We KNOW the persisted user is wrong but couldn't fetch the
        // right one — null it rather than render a foreign identity.
        set({ user: null })
      }
    } catch (error) {
      console.warn('[@quotech/auth] checkAuth: failed to fetch profile, continuing with token+org auth', error)
      if (identityMismatch) set({ user: null })
    }
  } else {
    const nextUser: User = {
      ...existingUser,
      organisation_id: existingUser.organisation_id || resolvedOrgId,
      organisation_name: existingUser.organisation_name || (resolvedOrgName || ''),
    }
    if (nextUser.organisation_id !== existingUser.organisation_id || nextUser.organisation_name !== existingUser.organisation_name) {
      set({ user: nextUser })
    }
  }

  return true
}
